import { useCheckoutSessionOwnership } from "@circle-react/components/Paywalls/Checkout/contexts/CheckoutSessionOwnershipContext";
import {
  CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_HEADER,
  CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_VERSION,
  CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER,
  CheckoutSessionInitializationError,
  checkoutSessionOwnershipCreationOptions,
  clearCheckoutClientRefreshAttempt,
  isCheckoutClientRefreshRequired,
  isTerminalCheckoutSessionCreationError,
  reloadCheckoutOnceForClientRefresh,
  type CheckoutSessionOwnershipToken,
} from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { checkoutSession as checkoutSessionUrls } from "@circle-react/helpers/urlHelpers/internalApi/paywalls/checkout/sessions";
import { useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import type { UseMutationResult } from "react-query";

import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import { updateSessionCache } from "../../helpers/sessions/updateSessionCache";

interface CreateCheckoutSessionParams {
  eventId?: number;
  paywallId: number;
  referrer?: string;
  rejectedSessionId?: number;
  returnTo?: string;
}

export const useCreateCheckoutSession = (): UseMutationResult<
  CheckoutSession,
  Error,
  CreateCheckoutSessionParams
> => {
  const queryClient = useQueryClient();
  const { storeTokenForSession } = useCheckoutSessionOwnership();
  const isFreshInitializationRetry = useRef(false);

  return useMutation<CheckoutSession, Error, CreateCheckoutSessionParams>({
    mutationFn: async (params: CreateCheckoutSessionParams) => {
      isFreshInitializationRetry.current =
        params.rejectedSessionId !== undefined;
      let ownershipToken: CheckoutSessionOwnershipToken | null = null;
      let ownershipProtocol: string | null = null;
      let session: CheckoutSession;

      try {
        session = await reactQueryPost<CheckoutSession>(
          checkoutSessionUrls.create(),
          {
            checkout_session: {
              paywall_id: params.paywallId,
              referrer: params.referrer,
              event_id: params.eventId,
              return_to: params.returnTo,
            },
          },
          {
            ...checkoutSessionOwnershipCreationOptions(),
            observeResponseHeaders: [
              {
                name: CHECKOUT_SESSION_OWNERSHIP_TOKEN_HEADER,
                onValue: value => {
                  ownershipToken = value || null;
                },
              },
              {
                name: CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_HEADER,
                onValue: value => {
                  ownershipProtocol = value || null;
                },
              },
            ],
          },
        );
      } catch (error: unknown) {
        if (isCheckoutClientRefreshRequired(error)) {
          reloadCheckoutOnceForClientRefresh();
        }
        throw error;
      }

      if (
        ownershipProtocol !== null &&
        ownershipProtocol !== CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_VERSION
      ) {
        throw new CheckoutSessionInitializationError();
      }

      if (
        ownershipProtocol === CHECKOUT_SESSION_OWNERSHIP_PROTOCOL_VERSION &&
        !ownershipToken
      ) {
        throw new CheckoutSessionInitializationError({
          canRetryWithFreshSession: true,
          rejectedSessionId: session.id,
        });
      }

      if (
        params.rejectedSessionId !== undefined &&
        session.id === params.rejectedSessionId
      ) {
        throw new CheckoutSessionInitializationError();
      }

      if (ownershipToken) {
        storeTokenForSession(session.id, ownershipToken);
      }
      clearCheckoutClientRefreshAttempt();

      return session;
    },
    onSuccess: (
      newSession: CheckoutSession,
      variables: CreateCheckoutSessionParams,
    ) => {
      updateSessionCache(queryClient, variables.paywallId, newSession);
    },
    retry: (failureCount, error) =>
      !isFreshInitializationRetry.current &&
      !isTerminalCheckoutSessionCreationError(error) &&
      failureCount < 3,
  });
};
